import * as React from 'react';
import { useState, useEffect } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';
import { FiCopy } from 'react-icons/fi';
import GridMui from '../../components/gridMui';
import api from '../../services/api';
import Loading from '../../components/loading';
import {
  Container,
  Flex,
  Card,
  CardHeader,
  CardContent,
  Badge,
} from '../../components/ui';
import { useTheme } from '../../context/ThemeContext';

const UploadedFilesPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [column, setColumn] = useState<any[]>([]);
  const [showNoData, setShowNoData] = useState<boolean>(false);
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
    components: {
      // @ts-expect-error - MuiDataGrid is from @mui/x-data-grid
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
            color: isDarkMode ? '#FFFFFF' : '#263238',
            border: `1px solid ${isDarkMode ? '#48484A' : '#E5E5EA'}`,
            '& .MuiDataGrid-cell': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeader': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
              '&:nth-of-type(even)': {
                backgroundColor: isDarkMode ? '#2C2C2E' : '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? '#3A3A3C' : '#F0F0F0',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
          },
        },
      },
    },
  });
  useEffect(() => {
    setShowNoData(false);
    api
      .get('/files')
      .then((response: any) => {
        if (response.data) {
          setData(
            response.data.map((x: any) => {
              return {
                id: x.fileId,
                url: `${process.env.REACT_APP_BACKEND_BASE_URL}/uploads/${x.filename}`,
                originalName: x.originalname,
                usergroups: [x.userGroupName],
              };
            }),
          );
          setShowNoData(true);
        }
      })
      .catch((error: any) => {
        console.error(error.response.data.message);
      });
  }, []);

  const handleClickUrlCopy = React.useCallback(
    (id: number) => {
      const test = data.find((x: any) => x.id === id);
      navigator.clipboard.writeText(`${test?.url}`);
    },
    [data],
  );

  const handleDelete = React.useCallback(
    (fileId: any): void => {
      // eslint-disable-next-line no-alert
      const confirmDelete = window.confirm(
        ` Are you sure you want to delete the file ${fileId} ?`,
      );
      if (confirmDelete) {
        api
          .delete(`/files/${fileId}`)
          .then(response => {
            if (response.status === 200) {
              setData([...data.filter(x => x.id !== fileId)]);
            } else {
              console.error(response);
            }
          })
          .catch(error => {
            console.error(error);
          });
      }
    },
    [data],
  );

  const RenderValue = (
    props: GridRenderCellParams<string[]>,
  ): React.ReactNode => {
    const { hasFocus, value } = props;
    const buttonElement = React.useRef<HTMLButtonElement | null>(null);
    const rippleRef = React.useRef<TouchRippleActions | null>(null);

    React.useLayoutEffect(() => {
      if (hasFocus) {
        const input = buttonElement.current?.querySelector('input');
        input?.focus();
      } else if (rippleRef.current) {
        rippleRef.current.stop({} as any);
      }
    }, [hasFocus]);

    const tags = value?.map((v: any, index: number) => {
      return (
        <Badge key={index} variant="secondary" size="small">
          {v}
        </Badge>
      );
    });

    return (
      <Flex
        direction="row"
        wrap="wrap"
        gap={1}
        style={{
          padding: '4px 0',
          height: '100%',
        }}
        align="center"
      >
        {tags}
      </Flex>
    );
  };

  useEffect(() => {
    if (data.length > 0) {
      const columns: GridColDef<(typeof data)[number]>[] = [
        { field: 'id', headerName: 'ID', width: 90 },

        {
          field: 'originalName',
          headerName: 'OriginalName',
          flex: 0.7,
          editable: false,
          width: 180,
        },
        {
          field: 'usergroups',
          headerName: 'Usergroups',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
          width: 180,
        },
        {
          field: 'copyUrl',
          headerName: 'Copy URL',
          align: 'center',
          width: 180,
          renderCell: params => (
            <FiCopy
              style={{ cursor: 'pointer', color: '#b2b2b2' }}
              onClick={(event: any) => {
                event.stopPropagation();
                handleClickUrlCopy(params.row.id);
              }}
            />
          ),
        },
      ];

      setColumn(columns);
    }
  }, [data.length, handleClickUrlCopy, handleDelete]);
  return (
    <Container maxWidth="full" padding>
      <Card variant="elevated" padding="large" fullHeight>
        <CardHeader>
          <h1 style={{ margin: 0 }}>Uploads</h1>
        </CardHeader>
        <CardContent>
          <ThemeProvider theme={muiTheme}>
            {data.length > 0 ? (
              <GridMui columns={column} rows={data} pageSizeOptions={20} />
            ) : showNoData ? (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '400px' }}
              >
                <span>No file uploaded yet</span>
              </Flex>
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '400px' }}
              >
                <Loading />
              </Flex>
            )}
          </ThemeProvider>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UploadedFilesPage;
